import React, { useEffect, useMemo, useState } from "react";
import * as htmlToImage from "html-to-image";

type Lang = "KR" | "EN";
type KRLevel = "상" | "중" | "하";
type ENLevel = "High" | "Middle" | "Low";
type Level = KRLevel | ENLevel;

type TextPackKR = {
  privacy: string;
  empathy: string[];
  insight: string[];
  guide: string[];
  close: string[];
  level: Record<KRLevel, string[]>;
};

type TextPackEN = {
  privacy: string;
  empathy: string[];
  insight: string[];
  guide: string[];
  close: string[];
  level: Record<ENLevel, string[]>;
};

const TEXT: { KR: TextPackKR; EN: TextPackEN } = {
  KR: {
    privacy: "입력 내용은 서버로 전송되지 않고, 이 브라우저에만 저장됩니다.",
    empathy: [
      "마음이 꽤 오래 흔들리고 있네요.",
      "쉽게 잊히지 않는 고민이군요.",
      "마음 한켠이 계속 무겁게 느껴지네요.",
      "지금 상황이 꽤 신경 쓰이는 것 같아요.",
      "생각이 계속 이어지는 상태군요.",
    ],
    insight: [
      "마음이 복잡할수록 판단이 흐려질 수 있어요.",
      "지금은 분석보다 관찰이 필요한 시점입니다.",
      "결정보다 과정을 먼저 살펴보세요.",
      "작은 성공부터 쌓아가는 게 좋아요.",
      "생각이 계속 맴돌 때는 한 걸음 뒤로 물러서보세요.",
    ],
    guide: [
      "오늘 할 일을 한 가지로 제한해보세요.",
      "속도를 조금 늦추고 진행하세요.",
      "완벽함보다 실천을 우선하세요.",
      "작은 성취를 매일 기록해보세요.",
      "오늘은 절반만 해도 충분합니다.",
    ],
    close: [
      "오늘 하루 수고 많으셨어요.",
      "지금 마음을 잠시 내려놓아도 괜찮아요.",
      "혼자가 아니니 안심하세요.",
      "오늘은 여기까지 해도 충분합니다.",
    ],
    level: {
      상: [
        "마음의 압박이 매우 강하게 느껴집니다.",
        "긴장감이 최고조에 달한 상태입니다.",
        "정서적 부담이 큰 시점입니다.",
      ],
      중: [
        "신경이 계속 쓰이는 상태입니다.",
        "중간 정도의 불안이 느껴집니다.",
        "마음이 계속 신경 쓰이는 상태예요.",
      ],
      하: [
        "가벼운 걱정 정도입니다.",
        "약간 신경이 쓰이는 상태예요.",
        "크게 부담되지 않는 상태입니다.",
      ],
    },
  },
  EN: {
    privacy: "Your input is not sent to any server. It stays in your browser.",
    empathy: [
      "I can feel that your mind has been unsettled for a while.",
      "This worry doesn’t fade easily.",
      "A part of your heart feels heavy.",
      "It seems like this situation is weighing on you.",
    ],
    insight: [
      "When your mind is busy, judgment can be clouded.",
      "This is a moment for observation more than analysis.",
      "Focus on the process rather than the decision.",
      "Start by building small successes.",
    ],
    guide: [
      "Limit your focus to one thing today.",
      "Slow down your pace a little.",
      "Progress matters more than perfection.",
      "Record your small achievements.",
    ],
    close: [
      "You did well today.",
      "It’s okay to put your mind down for a moment.",
      "You are not alone, remember that.",
    ],
    level: {
      High: [
        "Your mind is under intense pressure.",
        "Tension is at its peak.",
        "Emotional burden is high.",
        "Psychological pressure is strong.",
      ],
      Middle: [
        "Your mind keeps noticing things.",
        "Anxiety is moderate.",
        "Your mind continues to feel concerned.",
      ],
      Low: ["Worry is light.", "Slightly on your mind.", "Not a significant burden."],
    },
  },
};

// ---------- 유틸 ----------
function todayKey(): string {
  return new Date().toISOString().slice(0, 10).replace(/-/g, "");
}

function hash(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function pick(arr: string[], seed: number, o: number = 0): string {
  return arr[Math.floor(seededRandom(seed + o) * arr.length)];
}

// ---------- 사용 횟수 ----------
function getTodayCount(): number {
  return Number(localStorage.getItem("worryCnt_" + todayKey()) || 0);
}

function addCount(): void {
  localStorage.setItem("worryCnt_" + todayKey(), String(getTodayCount() + 1));
}

// ---------- 방문자(로컬 고유 방문자 느낌) ----------
function getDailyVisitors(): number {
  return Number(localStorage.getItem("visitors_" + todayKey()) || 0);
}

function markVisitOncePerDay(): void {
  const key = "visited_" + todayKey();
  const already = localStorage.getItem(key);
  if (already) return;

  localStorage.setItem(key, "1");
  localStorage.setItem(
    "visitors_" + todayKey(),
    String(getDailyVisitors() + 1)
  );
}

type ReportResult = { text: string; remain: number };

export default function App() {
  const DAILY_LIMIT = 3;

  const [birth, setBirth] = useState<string>("");
  const [worry, setWorry] = useState<string>("");
  const [lang, setLang] = useState<Lang>("KR");
  const [level, setLevel] = useState<Level>("중");
  const [result, setResult] = useState<ReportResult | null>(null);
  const [visitors, setVisitors] = useState<number>(0);

  const t = useMemo(() => (lang === "KR" ? TEXT.KR : TEXT.EN), [lang]);

  // 초기 로드
  useEffect(() => {
    const b = localStorage.getItem("savedBirth");
    if (b) setBirth(b);

    markVisitOncePerDay();
    setVisitors(getDailyVisitors());
  }, []);

  // 언어 변경 시 레벨 자동 매핑
  useEffect(() => {
    if (lang === "EN") {
      setLevel((prev) => {
        if (prev === "상") return "High";
        if (prev === "중") return "Middle";
        if (prev === "하") return "Low";
        return prev;
      });
    } else {
      setLevel((prev) => {
        if (prev === "High") return "상";
        if (prev === "Middle") return "중";
        if (prev === "Low") return "하";
        return prev;
      });
    }
  }, [lang]);

  function saveBirth(): void {
    localStorage.setItem("savedBirth", birth);
    alert(lang === "KR" ? "생년월일이 저장되었어요" : "Birthdate saved!");
  }

  function getSeed(): number {
    return hash(birth + worry + todayKey());
  }

  function buildReport(): string {
    const s = getSeed();

    if (lang === "KR") {
      const lv = level as KRLevel;
      return `
${pick(TEXT.KR.empathy, s, 1)}
${pick(TEXT.KR.level[lv], s, 2)}

${pick(TEXT.KR.insight, s, 3)}

${pick(TEXT.KR.guide, s, 4)}

${pick(TEXT.KR.close, s, 5)}
`.trim();
    }

    const lv = level as ENLevel;
    return `
${pick(TEXT.EN.empathy, s, 1)}
${pick(TEXT.EN.level[lv], s, 2)}

${pick(TEXT.EN.insight, s, 3)}

${pick(TEXT.EN.guide, s, 4)}

${pick(TEXT.EN.close, s, 5)}
`.trim();
  }

  function run(): void {
    if (!birth) {
      alert(lang === "KR" ? "생년월일을 입력해주세요" : "Please enter birthdate");
      return;
    }

    if (worry.length < 5 || worry.length > 50) {
      alert(lang === "KR" ? "걱정은 5~50자로 입력해주세요" : "Worry must be 5~50 characters");
      return;
    }

    const todayCount = getTodayCount();
    if (todayCount >= DAILY_LIMIT) {
      alert(
        lang === "KR"
          ? `오늘은 ${DAILY_LIMIT}회 모두 사용했어요 🌙`
          : `You've reached the ${DAILY_LIMIT}-times daily limit 🌙`
      );
      return;
    }

    addCount();
    const newCount = todayCount + 1;

    setResult({
      text: buildReport(),
      remain: DAILY_LIMIT - newCount,
    });
  }

  function share(): void {
    if (!result) return;
    navigator.clipboard.writeText(result.text);
    alert(lang === "KR" ? "리포트가 복사되었습니다" : "Report copied!");
  }

  function saveImage(): void {
    if (!result) return;
    const node = document.getElementById("resultCard");
    if (!node) return;

    htmlToImage.toPng(node).then((dataUrl: string) => {
      const a = document.createElement("a");
      a.download = "worry-report.png";
      a.href = dataUrl;
      a.click();
    });
  }

  const usedToday = getTodayCount();

  return (
    <div style={wrap}>
      <div style={card}>
        {/* ✅ 상단 작은 방문자 표시 */}
        <div style={topVisitorBar}>
          {lang === "KR" ? `오늘 방문자 : ${visitors}` : `Today's visitors : ${visitors}`}
        </div>

        <img
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?fit=crop&w=400&h=200"
          alt="nature"
          style={{ width: "100%", borderRadius: 12, marginBottom: 12 }}
        />

        <h2 style={{ textAlign: "center" }}>
          {lang === "KR" ? "너만의 AI 걱정인형" : "Worry-Doll AI"}
        </h2>

        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginBottom: 12 }}>
          <button
            style={{
              ...langBtn,
              backgroundColor: lang === "KR" ? "#5b5bd6" : "#fff",
              color: lang === "KR" ? "white" : "black",
            }}
            onClick={() => setLang("KR")}
          >
            KR
          </button>
          <button
            style={{
              ...langBtn,
              backgroundColor: lang === "EN" ? "#5b5bd6" : "#fff",
              color: lang === "EN" ? "white" : "black",
            }}
            onClick={() => setLang("EN")}
          >
            EN
          </button>
        </div>

        <label>{lang === "KR" ? "생년월일" : "Birthdate"}</label>
        <input type="date" value={birth} onChange={(e) => setBirth(e.target.value)} style={input} />
        <button style={subBtn} onClick={saveBirth}>
          {lang === "KR" ? "저장" : "Save"}
        </button>

        <label>{lang === "KR" ? "오늘의 걱정 (5~50자)" : "Today's Worry (5–50 chars)"}</label>
        <textarea value={worry} onChange={(e) => setWorry(e.target.value)} style={textarea} />
        <div style={{ fontSize: 12, textAlign: "right" }}>{worry.length}/50</div>

        <label>{lang === "KR" ? "걱정 강도" : "Worry Level"}</label>
        <select value={level} onChange={(e) => setLevel(e.target.value as Level)} style={input}>
          {lang === "KR" ? (
            <>
              <option value="하">하</option>
              <option value="중">중</option>
              <option value="상">상</option>
            </>
          ) : (
            <>
              <option value="Low">Low</option>
              <option value="Middle">Middle</option>
              <option value="High">High</option>
            </>
          )}
        </select>

        <button style={mainBtn} onClick={run}>
          {lang === "KR" ? "리포트 생성" : "Generate Report"} ({usedToday}/{DAILY_LIMIT})
        </button>

        {result && (
          <div id="resultCard" style={resultBox}>
            <h3>{lang === "KR" ? "오늘의 마음 리포트" : "Today's Worry Report"}</h3>
            <div style={{ whiteSpace: "pre-line" }}>{result.text}</div>

            <div style={{ marginTop: 10 }}>
              {lang === "KR" ? `남은 횟수: ${result.remain}` : `Remaining today: ${result.remain}`}
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button style={subBtn} onClick={share}>
                {lang === "KR" ? "복사" : "Copy"}
              </button>
              <button style={subBtn} onClick={saveImage}>
                {lang === "KR" ? "이미지 저장" : "Save Image"}
              </button>
            </div>
          </div>
        )}

        {/* 개인정보 안내 */}
        <div style={footerNote}>
          {lang === "KR" ? `KR: ${TEXT.KR.privacy}` : `EN: ${TEXT.EN.privacy}`}
        </div>
      </div>
    </div>
  );
}

/* ---------- 스타일 ---------- */
const wrap: React.CSSProperties = {
  minHeight: "100vh",
  background: "#f4f6fb",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: 16,
};

const card: React.CSSProperties = {
  width: 380,
  maxWidth: "100%",
  background: "white",
  padding: 24,
  borderRadius: 18,
  boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const topVisitorBar: React.CSSProperties = {
  fontSize: 11,
  color: "#555",
  background: "#f7f7ff",
  border: "1px solid #ececff",
  padding: "6px 10px",
  borderRadius: 12,
  textAlign: "center",
  marginBottom: 10,
};

const input: React.CSSProperties = {
  padding: 10,
  borderRadius: 10,
  border: "1px solid #ddd",
};

const textarea: React.CSSProperties = {
  padding: 10,
  borderRadius: 10,
  border: "1px solid #ddd",
  height: 90,
  resize: "none",
};

const mainBtn: React.CSSProperties = {
  marginTop: 10,
  padding: 12,
  borderRadius: 12,
  border: "none",
  background: "#5b5bd6",
  color: "white",
  fontWeight: 600,
  cursor: "pointer",
};

const subBtn: React.CSSProperties = {
  padding: 8,
  borderRadius: 10,
  border: "1px solid #ddd",
  background: "white",
  cursor: "pointer",
};

const langBtn: React.CSSProperties = {
  padding: "6px 16px",
  borderRadius: 10,
  border: "1px solid #ddd",
  cursor: "pointer",
  fontWeight: 600,
};

const resultBox: React.CSSProperties = {
  marginTop: 16,
  padding: 16,
  borderRadius: 14,
  background: "#f9fafc",
  border: "1px solid #eee",
};

const footerNote: React.CSSProperties = {
  marginTop: 10,
  fontSize: 11,
  color: "#666",
  lineHeight: 1.4,
};